import { useCallback, useEffect, useRef, useState } from 'react';
import { createSelectedContinuity } from '../services/selectedContinuity.service.js';
import { sameSelectedOwner, SELECTED_CONTINUITY_SCHEMA } from '../contracts/selectedContinuity.contract.js';
import { browserDeliveryClock } from '../clients/browserClock.client.js';
import { emitSelectedRequest } from '../clients/selectedSocket.client.js';

export function useSelectedContinuity({ getOwner, sequenceRef, recoverCut, applyReplay, recover }) {
  const ports = useRef({ getOwner, recoverCut, applyReplay, recover });
  ports.current = { getOwner, recoverCut, applyReplay, recover };
  const capability = useRef(null);
  const subscriptionRevision = useRef(0);
  const registration = useRef(null);
  const [pending, setPending] = useState(false);
  const service = useRef(null);
  if (!service.current) service.current = createSelectedContinuity({
    getOwner: () => {
      const owner = ports.current.getOwner();
      return { ...owner, supported: capability.current?.socket === owner.socket
        && capability.current?.generation === owner.generation };
    }, sequenceRef,
    recoverCut: options => ports.current.recoverCut(options),
    applyReplay: envelope => ports.current.applyReplay(envelope),
    recover: options => ports.current.recover(options),
    clock: browserDeliveryClock, onPending: setPending,
  });
  useEffect(() => { service.current.sync(); });
  useEffect(() => () => { subscriptionRevision.current += 1; registration.current = null; service.current.cancel(); }, []);
  const subscribe = useCallback((socket, payload) => {
    const owner = ports.current.getOwner();
    if (sameSelectedOwner(registration.current?.owner, owner)) return false;
    const revision = ++subscriptionRevision.current;
    registration.current = { owner, revision };
    return emitSelectedRequest(socket, 'subscribe:sportsnew:selected', payload, ack => {
      if (revision !== subscriptionRevision.current || !sameSelectedOwner(owner, ports.current.getOwner())) return;
      if (ack?.status !== true) { registration.current = null; return; }
      // Negotiated support is monotonic for this connection generation. An
      // unknown/failed response must never release an outstanding gap hold.
      if (ack.selectedContinuity?.schema === SELECTED_CONTINUITY_SCHEMA) {
        capability.current = { socket, generation: owner.generation };
      }
      service.current.sync();
    });
  }, []);
  const retire = useCallback(() => {
    subscriptionRevision.current += 1;
    registration.current = null;
    service.current.sync();
  }, []);
  return { continuity: service.current, pending, subscribe, retire };
}
